import type { User } from "../types";

/** Avoid clearing the admin roster when a snapshot/refresh build fails and returns {}. */
export function mergeRosterOrKeepPrevious(
  prev: Record<string, User>,
  next: Record<string, User>,
): Record<string, User> {
  if (Object.keys(next).length === 0 && Object.keys(prev).length > 0) {
    console.warn("[Admin] Keeping previous roster; new build returned empty.");
    return prev;
  }

  const staffFromPrev: Record<string, User> = {};
  for (const [k, u] of Object.entries(prev)) {
    const role = u.role;
    if (role === "employee" || role === "admin") {
      staffFromPrev[k] = u;
    }
  }
  const merged = { ...staffFromPrev, ...next };

  const prevKeys = Object.keys(prev).sort();
  const mergedKeys = Object.keys(merged).sort();
  if (
    prevKeys.length === mergedKeys.length &&
    prevKeys.every((k, i) => {
      if (k !== mergedKeys[i]) return false;
      const p = prev[k];
      const n = merged[k];
      return (
        p.email === n.email &&
        p.name === n.name &&
        p.role === n.role &&
        p.uid === n.uid &&
        p.emailVerified === n.emailVerified
      );
    })
  ) {
    return prev;
  }
  return merged;
}

/**
 * Dashboard roster from listTeam + listClients: API is source of truth when both calls succeed.
 * On partial failure, keep previous roster.
 */
export function applyDashboardRosterFromTeamAndClients(
  prev: Record<string, User>,
  teamResult: PromiseSettledResult<{ users?: unknown[] }>,
  clientsResult: PromiseSettledResult<{ users?: unknown[] }>,
): Record<string, User> {
  if (teamResult.status !== "fulfilled" || clientsResult.status !== "fulfilled") {
    return prev;
  }

  const team = (teamResult.value.users || []) as User[];
  const clients = (clientsResult.value.users || []) as User[];
  const next: Record<string, User> = {};
  for (const user of [...team, ...clients]) {
    const key = user.uid || user.email.toLowerCase();
    next[key] = user;
  }

  const prevKeys = Object.keys(prev).sort();
  const nextKeys = Object.keys(next).sort();
  if (
    prevKeys.length === nextKeys.length &&
    prevKeys.every((k, i) => {
      if (k !== nextKeys[i]) return false;
      const p = prev[k];
      const n = next[k];
      return (
        p.email === n.email &&
        p.name === n.name &&
        p.role === n.role &&
        p.uid === n.uid &&
        p.emailVerified === n.emailVerified
      );
    })
  ) {
    return prev;
  }
  return next;
}
